// Front-end copy of the SERVER's group rules (backend/services/gameRules.js), used for the group label
// ("Pure sequence" / "Impure sequence" / "Set" / "Invalid") and for building the declaration.
// Keep this in step with the server: same ordering (A low OR high, never wrap), same joker rules.
//
// classifyGroup(cards, wildJoker, burnedJokerIds) -> { isValid, kind, label, reason }
//   kind: 'pure' | 'impure' | 'set' | 'invalid'

const ORDER = { A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13 };
const order = (rank) => ORDER[rank] || 0;

const isPrinted = (c) => !!c && (c.isJoker || c.suit === 'joker' || c.rank === 'Joker');

// contextSuit: a wild-rank card in ITS OWN suit counts as a natural card in a pure sequence (server rule)
export function isJokerCard(card, wildJoker, contextSuit = null, burned = null) {
  if (!card) return false;
  if (isPrinted(card)) return true;
  if (!wildJoker) return false;
  if (burned && burned.has && burned.has(card.id)) return false;
  if (isPrinted(wildJoker)) {
    if (card.rank !== '2') return false;
    return !(contextSuit && card.suit === contextSuit);
  }
  if (card.rank === wildJoker.rank) return !(contextSuit && card.suit === contextSuit);
  return false;
}

// Gaps that jokers must fill for these distinct values (Ace low, or Ace high when an Ace is present)
function minGaps(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const gapsOf = (v) => v.reduce((g, x, i) => (i === 0 ? 0 : g + (x - v[i - 1] - 1)), 0);
  let best = gapsOf(sorted);
  if (sorted[0] === 1) best = Math.min(best, gapsOf([...sorted.slice(1), 14]));
  return best;
}

function pureSequence(cards, wildJoker, burned) {
  const suits = [...new Set(cards.map(c => c.suit))];
  const ctx = suits.length === 1 ? suits[0] : null;
  if (cards.some(c => isJokerCard(c, wildJoker, ctx, burned))) return false;
  if (suits.length !== 1) return false;
  const values = cards.map(c => order(c.rank));
  if (new Set(values).size !== values.length) return false;
  return minGaps(values) === 0;
}

export function classifyGroup(cards, wildJoker = null, burned = null) {
  const bad = (reason) => ({ isValid: false, kind: 'invalid', label: 'Invalid', reason });
  if (!cards || cards.length < 3) return bad('Need at least 3 cards');

  // Identical natural cards (same rank + suit) count as a pure sequence in this multi-deck game
  const suits0 = [...new Set(cards.map(c => c.suit))];
  const ctx0 = suits0.length === 1 ? suits0[0] : null;
  const jok0 = cards.filter(c => isJokerCard(c, wildJoker, ctx0, burned));
  const nat0 = cards.filter(c => !isJokerCard(c, wildJoker, ctx0, burned));
  if (nat0.length === 0 && cards.every(c => isPrinted(c) && c.isRed === true)) return { isValid: true, kind: 'pure', label: 'Pure sequence' };
  if (jok0.length === 0 && new Set(nat0.map(c => c.rank)).size === 1 && new Set(nat0.map(c => c.suit)).size === 1) {
    return { isValid: true, kind: 'pure', label: 'Pure sequence' };
  }

  if (pureSequence(cards, wildJoker, burned)) return { isValid: true, kind: 'pure', label: 'Pure sequence' };

  // Impure sequence: wild-rank cards ALWAYS act as jokers here (no context suit)
  const jokers = cards.filter(c => isJokerCard(c, wildJoker, null, burned));
  const natural = cards.filter(c => !isJokerCard(c, wildJoker, null, burned));
  if (natural.length === 0) {
    // 3+ jokers together are a valid impure sequence (server rule)
    return jokers.length >= 3 ? { isValid: true, kind: 'impure', label: 'Impure sequence' } : bad('Need at least one natural card');
  }
  const natSuits = [...new Set(natural.map(c => c.suit))];
  if (natSuits.length === 1) {
    const values = natural.map(c => order(c.rank));
    if (new Set(values).size === values.length && minGaps(values) <= jokers.length) {
      return { isValid: true, kind: 'impure', label: 'Impure sequence' };
    }
  }

  // Set: 3-4 cards, same rank, different suits, jokers fill the rest
  const setJokers = cards.filter(c => isPrinted(c) || (wildJoker && !(burned && burned.has && burned.has(c.id)) &&
    (isPrinted(wildJoker) ? c.rank === '2' : c.rank === wildJoker.rank)));
  const setNatural = cards.filter(c => !setJokers.includes(c));
  if (cards.length <= 4 && setNatural.length > 0 &&
      new Set(setNatural.map(c => c.rank)).size === 1 && new Set(setNatural.map(c => c.suit)).size === setNatural.length) {
    return { isValid: true, kind: 'set', label: 'Set' };
  }
  return bad('Not a valid sequence or set');
}
